import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AlertController, IonicModule, LoadingController, ModalController, NavController } from '@ionic/angular';
import { GoogleAuthProvider, getAuth, signInWithPopup, FacebookAuthProvider } from 'firebase/auth';
import { TranslocoModule, translate } from '@jsverse/transloco';
import { ProviderPlatform, SourcePlatform, SuggestedAction } from 'graphql/generated';
import { LoginForm } from '@app/core/types/authentication-form.type';
import { AuthService } from '@app/core/services/auth.service';
import { RegisterModalComponent } from '../register/auth-register-modal.component';
import { FormInputComponent } from '@app/core/components/forms/input/form-input.component';
import { FormPasswordComponent } from '@app/core/components/forms/password/form-password.component';
import { VerificationModalComponent } from '@app/core/components/modals/auth/verification/auth-verification-modal.component';
import { ForgotPasswordModalComponent } from '@app/core/components/modals/auth/forgot-password/auth-forgot-password-modal.component';

@Component({
  standalone: true,
  selector: 'ups-login-modal',
  templateUrl: './auth-login-modal.component.html',
  styleUrls: ['./auth-login-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    IonicModule,
    TranslocoModule,
    FormInputComponent,
    FormPasswordComponent
  ]
})
export class LoginModalComponent implements OnInit, OnDestroy {
  
  @ViewChild('btn') protected btn!: ElementRef;
  public readonly form: FormGroup<LoginForm> = new FormGroup({
    phoneCode: new FormControl('62', { nonNullable: true }),
    email: new FormControl<string | null>(null, { validators: Validators.email }),
    strategy: new FormControl<'email' | 'mobileNumber'>('email', { nonNullable: true }),
    password: new FormControl('', { nonNullable: true, validators: Validators.required }),
    mobileNumber: new FormControl<string | null>(null, [ Validators.pattern(/^[0-9]*$/) ])
  });
  public isLogin: boolean = false;
  protected sub?: Subscription;

  constructor (
    protected readonly ch: ChangeDetectorRef,
    protected readonly navCtrl: NavController,
    protected readonly authService: AuthService,
    protected readonly alertCtrl: AlertController,
    protected readonly modalCtrl: ModalController,
    protected readonly loadingCtrl: LoadingController
  ) { }

  public ngOnInit(): void{
    history.pushState({ login: true }, '');
  }

  public ngOnDestroy(): void{ 
    this.sub?.unsubscribe();
    if (history.state.login) history.back();
  }

  @HostListener('window:popstate', ['$event'])
  public popState(ev: PopStateEvent): void{ if (!ev.state?.login) this.modalCtrl.dismiss(); }

  public openRegister(): void{ this.openModal(RegisterModalComponent); }

  public back(): void{
    if (this.isLogin === false) this.modalCtrl.dismiss();
    else this.isLogin = false;
  }

  public normalLogin(strategy: 'email' | 'mobileNumber'): void{
    this.isLogin = true;
    this.form.patchValue({ strategy });
  }

  public forgotPassword(): void{
    const { strategy, email, mobileNumber, phoneCode } = this.form.value;

    if (!email && !mobileNumber) this.showAlert(translate('validation.required', { field: translate(`form.${ strategy }`) }));
    else {
      this.openModal(ForgotPasswordModalComponent, { strategy, creds: strategy === 'email'? email : `${ phoneCode }${ mobileNumber }` });
    }
  }

  public format(): void{
    let { strategy, mobileNumber } = this.form.value;
    if (strategy === 'mobileNumber') {
      mobileNumber = mobileNumber!.replace(/\D/g, '').replace(/^0+/,'');
      
      if (mobileNumber.substring(0, 2) === '62') mobileNumber = mobileNumber.substring(2);
      this.form.patchValue({ mobileNumber });
    }
  }

  public login(): void{
    const { strategy, password, email, mobileNumber, phoneCode } = this.form.value;
    
    if (password && this.form.valid) {
      this.toggleBtn();

      this.sub?.unsubscribe();
      this.sub = this.authService.login(strategy === 'email'? email! : `${ phoneCode }${ mobileNumber }`, password, strategy!).subscribe({
        next: ({ data }) => {
          this.modalCtrl.dismiss();
          if (data?.authMsLogin.suggestedAction === SuggestedAction.ChangePassword) this.navCtrl.navigateRoot('/profile', { queryParams: { action: SuggestedAction.ChangePassword }});
          else this.navCtrl.navigateRoot('/');
        },
        error: ({ graphQLErrors, message }: any) => {
          this.toggleBtn();
          this.showAlert(message, undefined, graphQLErrors[0].extensions.code === 'USER_NOT_CONFIRMED'? [{ text: 'OK', handler: () => this.resendVerification() }] : ['OK']);
        }
      });
    }
  }

  public async socialLogin(p: 'google' | 'facebook'): Promise<void>{
    const loader = await this.loadingCtrl.create();
    await loader.present();

    signInWithPopup(getAuth(), p === 'google'? new GoogleAuthProvider() : new FacebookAuthProvider()).then(({ user }) => {
      if (!user) {
        loader.dismiss();
        this.showAlert();

        return;
      } 
            
      user.getIdToken().then((token) => {
        this.sub?.unsubscribe();

        this.sub = this.authService.socialLogin({ idToken: token, providerPlatform: p.toUpperCase() as ProviderPlatform, sourcePlatform: SourcePlatform.CpCommerce }).subscribe({
          next: () => {
            loader.dismiss();
            this.modalCtrl.dismiss();
            this.navCtrl.navigateRoot('/');
          }
        });
      });
    }).catch(() => {
      loader.dismiss();
      this.showAlert();
    });
  }

  protected resendVerification(): void{
    if (this.form.value.strategy === 'mobileNumber') this.openModal(VerificationModalComponent, { phone: `${ this.form.value.phoneCode }${ this.form.value.mobileNumber }` });
    else {
      this.sub?.unsubscribe();

      this.sub = this.authService.resendVerification(this.form.value.email!, this.form.value.strategy!).subscribe({
        next: ({ data: { authMsResendEmailVerif }}: any) => this.showAlert(authMsResendEmailVerif, translate('title.success'))
      });
    }
  }

  protected toggleBtn(): void{
    this.btn?.nativeElement.toggleAttribute('disabled');
    this.btn?.nativeElement.classList.toggle('btn-loading');
  }
  
  protected async showAlert(message: string = translate('message.try_again'), header: string = translate('title.error'), btns: any[] = ['OK']): Promise<void>{ (await this.alertCtrl.create({ header, message, buttons: btns })).present(); }
  
  protected async openModal(component: any, componentProps: any = null): Promise<void>{
    const modal = await this.modalCtrl.create({ component, cssClass: 'folay-container full100-flex-col', componentProps }); 
    
    modal.present();
    modal.onDidDismiss().then(({ data: d }) => {
      if (d?.auto) {
        if (!d.cred) this.login();
        else {
          this.form.patchValue(d.cred);
          this.normalLogin(d.cred.strategy);
          this.ch.markForCheck();
        }
      } 
    });
  }
}
